// How a vendor's product list reads: what kind of thing each product is (the
// profile its type takes, not the store's purchase type), how many are in stock
// (instead of which app stores it is linked with — a store matter), and where
// it stands in review — a status dot on its picture (ReviewDot).
import React, { useEffect, useMemo, useState } from "react"
import { useContentTypes } from "../Store/extensions/shared/content-types"
import { useProductTypes, profileIdOf } from "../AdminShared/product-types/useProductTypes"
import { PUBLISH_STATUS, ATTENTION_CHIP } from "../AdminShared/ui"
import { VendorAttention } from "../AdminShared/api"
import { EPublishStatus } from "../Store/extensions/shared/editor-config"
import usePortalTexts from "./texts/usePortalTexts"
import { PRODUCTS } from "./texts/bundles"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

// the review state as a presence dot: green is on sale
const DOT = {
  [EPublishStatus.Draft]: "bg-admin_neutral_3",
  [EPublishStatus.InReview]: "bg-amber-400",
  [EPublishStatus.Published]: "bg-emerald-500",
  [EPublishStatus.Rejected]: "bg-admin_error",
  [EPublishStatus.ReviewRequired]: "bg-orange-500",
}

/**
 * Beside the product's name: "Review Required" when it waits for its vendor — sent back with what to change,
 * or on sale with changes a reviewer sent back (`attention`: the pids vendor-submissions {attention} answers).
 */
export const AttentionChip = ({ item, attention }) => {
  const t = usePortalTexts(PRODUCTS)
  if (Number(item?.publishStatus) !== EPublishStatus.ReviewRequired && !attention?.has(item?.pid)) return null
  return (
    <span className={`shrink-0 inline-flex items-center h-6 px-2.5 rounded-full text-xs font-semibold whitespace-nowrap ${ATTENTION_CHIP}`} data-testid="product-attention">
      {t`Review Required`}
      <HelpHint helpKey="vendor.products#attention" className="ml-1 -mr-1" />
    </span>
  )
}

/** A product's review state, as a status dot ringed like a chat's online mark. */
export const ReviewDot = ({ status }) => {
  const t = usePortalTexts(PRODUCTS)
  const state = PUBLISH_STATUS.find((p) => p.value === Number(status)) || PUBLISH_STATUS[0]
  return (
    <span
      className={`block w-3.5 h-3.5 rounded-full ring-2 ring-admin_paper ${DOT[state.value] || DOT[0]}`}
      title={t(state.label)}
      aria-label={t(state.label)}
      data-testid="product-review-state"
      data-state={state.value}
    />
  )
}

/** The company's products that wait for its vendor (a Set of pids), asked again when `version` moves. */
export function useAttention(orgId, version) {
  const [pids, setPids] = useState(() => new Set())
  useEffect(() => {
    if (!orgId) return setPids(new Set())
    let alive = true
    VendorAttention(orgId)
      .then((res) => alive && setPids(new Set(Array.isArray(res?.pids) ? res.pids : [])))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [orgId, version])
  return pids
}

/** Column overrides for the store products browser in a vendor's views. */
export default function useVendorProductColumns() {
  const contentTypes = useContentTypes()
  const { bySlug } = useProductTypes()
  const t = usePortalTexts(PRODUCTS)
  return useMemo(() => {
    const byId = new Map(contentTypes.map((type) => [type.contentId, type]))
    return {
      type: {
        title: <span className="inline-flex items-center gap-1">{t`Type`}<HelpHint helpKey="vendor.products#list" /></span>,
        sortable: false,
        render: (_, item) => {
          // the profile of the product's type (or the nearest one above it)
          const id = item.profile?.id || profileIdOf(bySlug, item.productType)
          const type = byId.get(id)
          if (!type && !item.profile) return <span className="text-admin_text_secondary text-sm">—</span>
          return (
            <span className="text-sm" data-testid="product-content-type">
              {type?.name || item.profile?.name || id}
            </span>
          )
        },
      },
      // how many are left to sell; none counted is none limited
      platforms: {
        title: <span className="inline-flex items-center gap-1">{t`In Stock`}<HelpHint helpKey="vendor.products#list" /></span>,
        sortable: false,
        // a click opens the product at its Availability tab, where the stock is set
        render: (_, item, { open } = {}) => (
          <button
            type="button"
            onClick={() => open?.("inventory")}
            title={t`Edit stock`}
            className={`text-sm tabular-nums rounded px-1.5 -mx-1.5 py-0.5 hover:bg-admin_primary/10 hover:text-admin_primary ${
              typeof item.stock !== "number" ? "text-admin_text_secondary" : item.stock <= 0 ? "text-admin_error font-semibold" : ""
            }`}
            data-testid="product-stock"
          >
            {typeof item.stock === "number" ? item.stock : "∞"}
          </button>
        ),
      },
    }
  }, [contentTypes, bySlug, t])
}
